
import Vue from 'vue';
import { CompanyUserRole, CompanyUserRoleDef } from '@run-diffusion/shared';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins';
import { MemberRow } from '@/utils/membersApi';
import RoleDefBadge from '@/views/Team/CompanyDashboard/RoleDefBadge.vue';

/**
 * The person's company role, live: `companyUserRoles/{companyId}_{userId}`
 * bound per cell and resolved against the page-bound role defs, exactly as
 * the team-role cell does. A pending invite has no userId yet, so it shows
 * the role the stub will grant on acceptance.
 */
export default Vue.extend({
	name: 'MemberCompanyRoleTableCell',
	props: {
		// Only the account is read, so a directory row and a team's membership
		// doc (the member dashboard's header) serve equally.
		row: {
			type: Object as () => Pick<MemberRow, 'userId'>,
			required: true,
		},
		companyId: {
			type: String,
			default: null,
		},
		companyUserRoleDefsMap: {
			type: Object as () => Record<string, CompanyUserRoleDef>,
			default: () => ({}),
		},
		stubCompanyUserRoleDefId: {
			type: String,
			default: null,
		},
		// Show nothing, rather than the badge's "No Role", for someone without a
		// company role: in a table column a blank cell already says it.
		blankWhenNone: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			loading: false,
			// One per bindCompanyUserRole call. A rebind (or the null branch)
			// unbinds the listener an earlier call is still awaiting, and that
			// call's promise then never settles, so its finally never runs. Only
			// the call holding the current token may touch `loading`.
			bindToken: 0,
			localCompanyUserRole: null as CompanyUserRole | null,
		};
	},
	computed: {
		userId (): string | null {
			return this.row?.userId || null;
		},
		currentRoleDefId (): string | null {
			if (this.userId) {
				return this.localCompanyUserRole?.roleDefId || null;
			}
			return this.stubCompanyUserRoleDefId || null;
		},
		isPendingStub (): boolean {
			return !this.userId && !!this.stubCompanyUserRoleDefId;
		},
		roleDef (): CompanyUserRoleDef | null {
			if (!this.currentRoleDefId) return null;
			return this.companyUserRoleDefsMap?.[this.currentRoleDefId] || null;
		},
	},
	watch: {
		userId: {
			immediate: true,
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal !== oldVal) {
					this.bindCompanyUserRole(this.companyId, newVal);
				}
			},
		},
		companyId: {
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal !== oldVal) {
					this.bindCompanyUserRole(newVal, this.userId);
				}
			},
		},
		localCompanyUserRole (newVal: CompanyUserRole | null) {
			// The bound doc changed under a live cell (role assigned, changed, or
			// removed elsewhere): tell the row so its actions follow.
			if (this.userId) this.reportBoundRoleDefId(newVal);
		},
	},
	methods: {
		reportBoundRoleDefId (role: CompanyUserRole | null) {
			this.$emit('bound-role-def-id', role?.roleDefId || null);
		},
		async bindCompanyUserRole (companyId: string | null, userId: string | null) {
			const token: number = ++this.bindToken;
			if (!companyId || !userId) {
				if (this.$firestoreRefs?.localCompanyUserRole) this.$unbind('localCompanyUserRole');
				this.localCompanyUserRole = null;
				// A bind superseded here may never settle; its loading ends now.
				this.loading = false;
				// Nothing live to report: the row falls back to the stub and the Postgres flag.
				this.$emit('bound-role-def-id', undefined);
				return;
			}

			this.loading = true;
			try {
				const roleId: string = `${companyId}_${userId}`;
				const roleRef = db.collection('companyUserRoles').doc(roleId);
				await this.$bind(
					'localCompanyUserRole',
					roleRef,
					get$bindFirestoreOptions({ maxRefDepth: 0 }), // maxRefDepth: 0 because users don't have access to other users' document data
				);
				if (token === this.bindToken) this.reportBoundRoleDefId(this.localCompanyUserRole);
			} catch (error) {
				console.error('Error binding company user role:', error);
			} finally {
				if (token === this.bindToken) this.loading = false;
			}
		},
	},
	components: {
		RoleDefBadge,
	},
});
